import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Download, RefreshCw, BarChart3, Eye, Lightbulb, Globe, FileText, X, ExternalLink, ArrowLeft } from 'lucide-react'
import AgentHeader from '../../components/AgentHeader'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, LineElement, PointElement, Filler } from 'chart.js'
import { Bar, Pie, Line, Doughnut } from 'react-chartjs-2'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import LoadingSkeleton from '../../components/LoadingSkeleton'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  Filler
)

const ChartResultPage = ({ 
  query, 
  inputType, 
  inputData, 
  generatedCharts, 
  progress, 
  isLoading, 
  isCompleted, 
  onBack, 
  onRegenerateChart, 
  error,
  searchResults 
}) => {
  const [selectedChart, setSelectedChart] = useState(null)
  const [showCitations, setShowCitations] = useState(false)
  const chartRefs = useRef({})

  // Chart color schemes
  const colorSchemes = {
    pie: {
      backgroundColor: [
        'rgba(255, 99, 132, 0.8)',
        'rgba(54, 162, 235, 0.8)',
        'rgba(255, 205, 86, 0.8)',
        'rgba(75, 192, 192, 0.8)',
        'rgba(153, 102, 255, 0.8)',
        'rgba(255, 159, 64, 0.8)'
      ],
      borderColor: [
        'rgba(255, 99, 132, 1)',
        'rgba(54, 162, 235, 1)',
        'rgba(255, 205, 86, 1)',
        'rgba(75, 192, 192, 1)',
        'rgba(153, 102, 255, 1)',
        'rgba(255, 159, 64, 1)'
      ]
    },
    bar: {
      backgroundColor: 'rgba(54, 162, 235, 0.8)',
      borderColor: 'rgba(54, 162, 235, 1)'
    },
    line: {
      backgroundColor: 'rgba(75, 192, 192, 0.2)',
      borderColor: 'rgba(75, 192, 192, 1)',
      pointBackgroundColor: 'rgba(75, 192, 192, 1)'
    },
    area: {
      backgroundColor: 'rgba(153, 102, 255, 0.2)',
      borderColor: 'rgba(153, 102, 255, 1)',
      pointBackgroundColor: 'rgba(153, 102, 255, 1)'
    }
  }

  const getChartData = (chart) => {
    const { labels, values } = chart.data
    
    switch (chart.type) {
      case 'pie':
      case 'doughnut':
        return {
          labels,
          datasets: [{
            data: values,
            backgroundColor: colorSchemes.pie.backgroundColor,
            borderColor: colorSchemes.pie.borderColor,
            borderWidth: 2
          }]
        }
      
      case 'bar':
        return {
          labels,
          datasets: [{
            label: chart.title,
            data: values,
            backgroundColor: colorSchemes.bar.backgroundColor,
            borderColor: colorSchemes.bar.borderColor,
            borderWidth: 2
          }]
        }
      
      case 'line':
        return {
          labels,
          datasets: [{
            label: chart.title,
            data: values,
            backgroundColor: colorSchemes.line.backgroundColor,
            borderColor: colorSchemes.line.borderColor,
            pointBackgroundColor: colorSchemes.line.pointBackgroundColor,
            borderWidth: 3,
            pointRadius: 5,
            pointHoverRadius: 7,
            tension: 0.4
          }]
        }
      
      case 'area':
        return {
          labels,
          datasets: [{
            label: chart.title,
            data: values,
            backgroundColor: colorSchemes.area.backgroundColor,
            borderColor: colorSchemes.area.borderColor,
            pointBackgroundColor: colorSchemes.area.pointBackgroundColor,
            borderWidth: 3,
            pointRadius: 5,
            pointHoverRadius: 7,
            fill: true,
            tension: 0.4
          }]
        }
      
      default:
        return { labels: [], datasets: [] }
    }
  }

  const getChartOptions = (chart) => {
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: '#e5e7eb',
            padding: 20,
            font: {
              size: 12
            }
          }
        },
        title: {
          display: true,
          text: chart.title,
          color: '#f9fafb',
          font: {
            size: 16,
            weight: 'bold'
          },
          padding: 20
        }
      }
    }

    if (chart.type === 'bar' || chart.type === 'line' || chart.type === 'area') {
      baseOptions.scales = {
        x: {
          ticks: {
            color: '#9ca3af'
          },
          grid: {
            color: 'rgba(156, 163, 175, 0.1)'
          }
        },
        y: {
          ticks: {
            color: '#9ca3af'
          },
          grid: {
            color: 'rgba(156, 163, 175, 0.1)'
          }
        }
      }
    }

    return baseOptions
  }

  const renderChart = (chart) => {
    const chartData = getChartData(chart)
    const chartOptions = getChartOptions(chart)

    switch (chart.type) {
      case 'pie':
        return <Pie ref={el => chartRefs.current[chart.id] = el} data={chartData} options={chartOptions} />
      case 'bar':
        return <Bar ref={el => chartRefs.current[chart.id] = el} data={chartData} options={chartOptions} />
      case 'line':
        return <Line ref={el => chartRefs.current[chart.id] = el} data={chartData} options={chartOptions} />
      case 'doughnut':
        return <Doughnut ref={el => chartRefs.current[chart.id] = el} data={chartData} options={chartOptions} />
      case 'area':
        return <Line ref={el => chartRefs.current[chart.id] = el} data={chartData} options={chartOptions} />
      default:
        return <div className="text-text-secondary">Chart type not supported</div>
    }
  }

  const downloadChart = (chart) => {
    const chartRef = chartRefs.current[chart.id]
    if (chartRef && chartRef.canvas) {
      const canvas = chartRef.canvas
      const url = canvas.toDataURL('image/png', 1.0)
      const link = document.createElement('a')
      link.download = `${chart.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_chart.png`
      link.href = url
      link.click()
    }
  }

  const downloadAllCharts = async () => {
    const zip = new JSZip()
    
    // Create a promise for each chart to be processed
    const chartPromises = generatedCharts.map((chart, index) => {
      return new Promise((resolve) => {
        // Small delay to ensure chart is rendered
        setTimeout(() => {
          const chartRef = chartRefs.current[chart.id]
          if (chartRef && chartRef.canvas) {
            const canvas = chartRef.canvas
            canvas.toBlob((blob) => {
              if (blob) {
                const fileName = `${chart.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_chart.png`
                zip.file(fileName, blob)
              }
              resolve()
            }, 'image/png', 1.0)
          } else {
            resolve()
          }
        }, index * 100) // Small delay between processing each chart
      })
    })
    
    // Wait for all charts to be processed
    await Promise.all(chartPromises)
    
    // Generate and download the zip file
    try {
      const content = await zip.generateAsync({ type: 'blob' })
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')
      const zipFileName = `charts_${timestamp}.zip`
      saveAs(content, zipFileName)
    } catch (error) {
      console.error('Error creating zip file:', error)
      // Fallback to individual downloads if zip creation fails
      generatedCharts.forEach((chart, index) => {
        setTimeout(() => {
          downloadChart(chart)
        }, index * 500)
      })
    }
  }

  if (error) {
    return (
      <div className="min-h-screen bg-bg-primary">
        <AgentHeader
          onBack={onBack}
          icon={BarChart3}
          title="Chart Agent"
          query="Error"
          iconColor="text-emerald-600"
          iconBgColor="bg-emerald-600/10"
          actions={[]}
        />
        
        <div className="p-4">
          <div className="max-w-6xl mx-auto">
            <div className="bg-bg-secondary rounded-xl border border-border-primary p-6">
              <div className="text-center py-8">
                <div className="text-red-400 text-lg font-medium mb-2">Terjadi Kesalahan</div>
                <div className="text-text-secondary mb-4">{error}</div>
                <button
                  onClick={onBack}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Kembali
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bg-primary">
      {/* Header */}
      <AgentHeader
        onBack={onBack}
        icon={BarChart3}
        title="Chart Agent"
        query={inputType === 'topic' ? 'Topik Chart' : 'Data Input'}
        iconColor="text-emerald-600"
        iconBgColor="bg-emerald-600/10"
        actions={[]}
      />
      
      <div className="p-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-bg-secondary rounded-xl border border-border-primary p-6">

          {/* Query Display */}
          <div className="bg-bg-tertiary rounded-lg p-4 mb-6 border border-border-secondary">
            <h2 className="text-lg font-semibold text-text-primary mb-2">
              {inputType === 'topic' ? 'Topik Chart' : 'Data Input'}
            </h2>
            <p className="text-text-secondary">{query}</p>
            {inputType === 'data' && (
              <div className="mt-3 p-3 bg-bg-primary rounded border border-border-secondary">
                <p className="text-sm text-text-secondary whitespace-pre-wrap">{inputData}</p>
              </div>
            )}
            
            {/* Data Source Information */}
            {isCompleted && generatedCharts.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-800">Sumber Data</h3>
                </div>
                <div className="text-blue-700 text-sm space-y-1">
                  {(() => {
                    const firstChart = generatedCharts[0]
                    const metadata = firstChart?.metadata
                    
                    if (metadata?.dataSource === 'linkup_search') {
                      return (
                        <>
                          <p>✅ Data real-time dari web melalui <strong>Search API</strong></p>
                          <p>🤖 Dianalisis oleh <strong>AI GENIUS Model</strong> untuk insight yang akurat</p>
                          <p>📊 {metadata.searchResultsCount} hasil pencarian diproses</p>
                          <p>⏰ Dibuat pada: {new Date(metadata.generatedAt).toLocaleString('id-ID')}</p>
                        </>
                      )
                    } else if (metadata?.dataSource === 'manual_input') {
                      return (
                        <>
                          <p>📝 Data dari input manual pengguna</p>
                          <p>⚙️ Diproses dengan <strong>algoritma data processing</strong></p>
                          <p>📊 {metadata.inputDataLength} data point dianalisis</p>
                          <p>⏰ Dibuat pada: {new Date(metadata.generatedAt).toLocaleString('id-ID')}</p>
                        </>
                      )
                    } else if (metadata?.dataSource === 'fallback_data') {
                      return (
                        <>
                          <p>🔄 Data fallback berdasarkan pola topik</p>
                          <p>🎯 Menggunakan <strong>pattern-based analysis</strong></p>
                          <p>⏰ Dibuat pada: {new Date(metadata.generatedAt).toLocaleString('id-ID')}</p>
                        </>
                      )
                    } else {
                      return <p>Chart dibuat dengan teknologi AI untuk visualisasi data yang optimal.</p>
                    }
                  })()
                  }
                </div>
              </div>
            )}
          </div>

          {/* Progress */}
          {isLoading && (
            <div className="mb-6">
              <LoadingSkeleton 
                message={`Generating chart ${progress.current + 1} dari ${progress.total}...`}
                submessage="Membuat visualisasi data yang menarik"
              />
            </div>
          )}

          {/* Action Buttons */}
          {isCompleted && (
            <div className="flex gap-3 mb-6">
              <button
                onClick={downloadAllCharts}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all"
              >
                <Download className="w-4 h-4" />
                Download Semua Chart
              </button>
              <button
                onClick={() => setShowCitations(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-lg hover:from-purple-700 hover:to-violet-700 transition-all"
              >
                <FileText className="w-4 h-4" />
                Lihat Referensi
              </button>
            </div>
          )}

          {/* Charts Stack */}
          <div className="space-y-6">
            {generatedCharts.map((chart, index) => (
              <div key={chart.id} className="bg-bg-tertiary rounded-lg border border-border-secondary p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-text-primary capitalize">{chart.type} Chart</h3>
                    {(() => {
                      const metadata = chart?.metadata
                      if (metadata?.dataSource === 'linkup_search') {
                        return (
                          <div className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-green-500 to-blue-500 text-white text-xs rounded-full font-medium shadow-lg">
                            AI+Web
                          </div>
                        )
                      } else if (metadata?.dataSource === 'manual_input') {
                        return (
                          <div className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-xs rounded-full font-medium shadow-lg">
                            Manual
                          </div>
                        )
                      } else if (metadata?.dataSource === 'fallback_data') {
                        return (
                          <div className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs rounded-full font-medium shadow-lg">
                            Pattern
                          </div>
                        )
                      }
                      return null
                    })()}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedChart(chart)}
                      className="p-2 hover:bg-bg-primary rounded-lg transition-colors"
                      title="Preview"
                    >
                      <Eye className="w-4 h-4 text-text-secondary" />
                    </button>
                    <button
                      onClick={() => downloadChart(chart)}
                      className="p-2 hover:bg-bg-primary rounded-lg transition-colors"
                      title="Download"
                    >
                      <Download className="w-4 h-4 text-text-secondary" />
                    </button>
                    <button
                      onClick={() => onRegenerateChart(chart.id)}
                      className="p-2 hover:bg-bg-primary rounded-lg transition-colors"
                      title="Regenerate"
                    >
                      <RefreshCw className="w-4 h-4 text-text-secondary" />
                    </button>
                  </div>
                </div>
                
                <div className="h-64 mb-4">
                  {renderChart(chart)}
                </div>
                
                <div className="text-sm text-text-secondary">
                  <p className="mb-2">{chart.description}</p>
                  {chart.insights && chart.insights.length > 0 && (
                    <div className="space-y-1">
                      {chart.insights.slice(0, 2).map((insight, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <Lightbulb className="w-3 h-3 text-yellow-400 mt-0.5 flex-shrink-0" />
                          <span className="text-xs">{insight}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Chart Detail Modal */}
          {selectedChart && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <div className="bg-bg-secondary rounded-xl border border-border-primary p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-text-primary">{selectedChart.title}</h2>
                  <button
                    onClick={() => setSelectedChart(null)}
                    className="p-2 hover:bg-bg-tertiary rounded-lg transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5 text-text-secondary" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="h-96">
                    {renderChart(selectedChart)}
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-text-primary mb-2">Deskripsi</h3>
                      <p className="text-text-secondary">{selectedChart.description}</p>
                    </div>
                    
                    {selectedChart.insights && selectedChart.insights.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-text-primary mb-2">Insights</h3>
                        <div className="space-y-2">
                          {selectedChart.insights.map((insight, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <Lightbulb className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-text-secondary">{insight}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <h3 className="font-semibold text-text-primary mb-2">Data</h3>
                      <div className="bg-bg-tertiary rounded-lg p-3 border border-border-secondary">
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {selectedChart.data.labels.map((label, idx) => (
                            <div key={idx} className="flex justify-between">
                              <span className="text-text-secondary">{label}:</span>
                              <span className="text-text-primary font-medium">{selectedChart.data.values[idx]}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Data Source Details */}
                    {inputType === 'topic' && (
                      <div>
                        <h3 className="font-semibold text-text-primary mb-2">Metodologi Data</h3>
                        <div className="bg-bg-tertiary rounded-lg p-3 border border-border-secondary">
                          <div className="space-y-2 text-sm">
                            <div className="flex items-start gap-2">
                              <div className="w-2 h-2 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></div>
                              <div>
                                <span className="text-text-primary font-medium">Pencarian Data:</span>
                                <span className="text-text-secondary ml-1">Search API</span>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <div className="w-2 h-2 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></div>
                              <div>
                                <span className="text-text-primary font-medium">Analisis AI:</span>
                                <span className="text-text-secondary ml-1">LLM GENIUS Model</span>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <div className="w-2 h-2 bg-purple-400 rounded-full mt-1.5 flex-shrink-0"></div>
                              <div>
                                <span className="text-text-primary font-medium">Validasi:</span>
                                <span className="text-text-secondary ml-1">Data terverifikasi dan diproses</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => downloadChart(selectedChart)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all"
                      >
                        <Download className="w-4 h-4" />
                        Download Chart
                      </button>
                      <button
                        onClick={() => onRegenerateChart(selectedChart.id)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Regenerate
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Citations Modal */}
          {showCitations && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-bg-secondary rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden border border-border-primary">
                <div className="flex items-center justify-between p-6 border-b border-border-primary">
                  <h2 className="text-xl font-bold text-text-primary">Referensi Sumber Data</h2>
                  <button
                    onClick={() => setShowCitations(false)}
                    className="p-2 hover:bg-bg-tertiary rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-text-secondary" />
                  </button>
                </div>
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                  {(() => {
                    // Get search results from chart metadata or props
                    const sources = searchResults || (generatedCharts.length > 0 && generatedCharts[0].metadata?.searchResults) || []
                    
                    if (sources.length > 0) {
                      return (
                        <div className="space-y-4">
                          {sources.map((source, index) => (
                            <div key={index} className="border border-border-secondary rounded-lg p-4 bg-bg-tertiary">
                              <div className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                  {index + 1}
                                </div>
                                <div className="flex-1">
                                  <h3 className="font-medium text-text-primary mb-2">
                                    {source?.title || 'Untitled Source'}
                                  </h3>
                                  {source?.snippet && (
                                    <p className="text-text-secondary text-sm mb-2 leading-relaxed">
                                      {source.snippet}
                                    </p>
                                  )}
                                  {(source?.url || source?.link) && (
                                    <a
                                      href={source.url || source.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm transition-colors"
                                    >
                                      <span>Buka Sumber</span>
                                      <ExternalLink className="w-3 h-3" />
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    } else {
                      return (
                        <div className="text-center py-8 text-text-secondary">
                          <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>Tidak ada referensi yang tersedia</p>
                          <p className="text-sm mt-1">Data chart dibuat dari input manual atau tidak menggunakan sumber web</p>
                        </div>
                      )
                    }
                  })()
                  }
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  )
}

ChartResultPage.propTypes = {
  query: PropTypes.string.isRequired,
  inputType: PropTypes.string.isRequired,
  inputData: PropTypes.string,
  generatedCharts: PropTypes.array.isRequired,
  progress: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isCompleted: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  onRegenerateChart: PropTypes.func.isRequired,
  error: PropTypes.string,
  searchResults: PropTypes.array
}

export default ChartResultPage